"use client";

import { useId, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { PortableText, type PortableTextComponents } from "@portabletext/react";
import type { TypedObject } from "@portabletext/types";

import { urlFor } from "@/lib/sanity";

type TocItem = {
  id: string;
  level: 2 | 3 | 4;
  title: string;
};

type PortableTextSpan = {
  _type: "span";
  text?: string;
};

type PortableTextBlock = {
  _key?: string;
  _type: "block";
  style?: string;
  children?: PortableTextSpan[];
};

type SanityTableRow = {
  _key?: string;
  _type?: string;
  cells?: unknown[];
  isHeader?: boolean;
};

type SanityTable = {
  _key?: string;
  _type: "table";
  rows?: SanityTableRow[];
};

function getTableRows(value: unknown): SanityTableRow[] {
  const table = value as SanityTable;
  return Array.isArray(table?.rows) ? table.rows : [];
}

function getCellText(cell: unknown): string {
  if (cell == null) return "";
  if (typeof cell === "string") return cell;
  if (typeof cell === "number" || typeof cell === "boolean") return String(cell);
  return "";
}

function getBlockPlainText(block: unknown): string {
  const b = block as PortableTextBlock;
  const children = Array.isArray(b?.children) ? b.children : [];
  return children
    .map((child) => (typeof child?.text === "string" ? child.text : ""))
    .join("")
    .trim();
}

function slugifyHeading(text: string): string {
  const base = text
    .trim()
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "");

  const slug = base
    .replace(/\s+/g, "-")
    .replace(/[/?#%&.,"'`~:;!@#$^*()+=<>\[\]{}|\\]/g, "")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

  return slug || "section";
}

function headingIdFor(block: unknown): string {
  const b = block as PortableTextBlock;
  const title = getBlockPlainText(block);
  const key = typeof b?._key === "string" ? b._key : "";

  const slug = slugifyHeading(title);
  return key ? `${slug}-${key}` : slug;
}

function buildToc(value: TypedObject | TypedObject[]): TocItem[] {
  const blocks = Array.isArray(value) ? value : [value];

  const items: TocItem[] = [];
  for (const block of blocks) {
    const b = block as PortableTextBlock;
    if (b?._type !== "block") continue;

    const style = typeof b.style === "string" ? b.style : "";
    const level = style === "h2" ? 2 : style === "h3" ? 3 : style === "h4" ? 4 : null;
    if (!level) continue;

    const title = getBlockPlainText(b);
    if (!title) continue;

    items.push({
      id: headingIdFor(b),
      level,
      title,
    });
  }

  return items;
}

function getYouTubeId(input?: string): string | null {
  if (!input) return null;
  const trimmed = input.trim();
  if (!trimmed) return null;

  // Allow plain videoId
  if (/^[a-zA-Z0-9_-]{11}$/.test(trimmed)) return trimmed;

  try {
    const url = new URL(trimmed);
    const host = url.hostname.replace(/^www\./, "");

    if (host === "youtu.be") {
      const id = url.pathname.split("/").filter(Boolean)[0];
      return id && /^[a-zA-Z0-9_-]{11}$/.test(id) ? id : null;
    }

    if (host === "youtube.com" || host === "m.youtube.com") {
      const v = url.searchParams.get("v");
      if (v && /^[a-zA-Z0-9_-]{11}$/.test(v)) return v;

      const parts = url.pathname.split("/").filter(Boolean);
      const embedIndex = parts.indexOf("embed");
      if (embedIndex !== -1) {
        const id = parts[embedIndex + 1];
        return id && /^[a-zA-Z0-9_-]{11}$/.test(id) ? id : null;
      }

      const shortsIndex = parts.indexOf("shorts");
      if (shortsIndex !== -1) {
        const id = parts[shortsIndex + 1];
        return id && /^[a-zA-Z0-9_-]{11}$/.test(id) ? id : null;
      }
    }

    return null;
  } catch {
    return null;
  }
}

const components: PortableTextComponents = {
  types: {
    image: ({ value }) => {
      if (!value?.asset) return null;

      const displayWidth =
        typeof value?.displayWidth === "number" ? value.displayWidth : undefined;
      const displayHeight =
        typeof value?.displayHeight === "number" ? value.displayHeight : undefined;

      const intrinsicWidth =
        typeof value?.dimensions?.width === "number" ? value.dimensions.width : undefined;
      const intrinsicHeight =
        typeof value?.dimensions?.height === "number" ? value.dimensions.height : undefined;

      const aspectRatio =
        displayWidth && displayHeight
          ? `${displayWidth} / ${displayHeight}`
          : intrinsicWidth && intrinsicHeight
            ? `${intrinsicWidth} / ${intrinsicHeight}`
            : "16 / 9";

      return (
        <div
          className="my-4 sm:my-6 relative w-full bg-[var(--light-blue)] rounded-2xl overflow-hidden"
          style={{ aspectRatio }}
        >
          <Image
            src={urlFor(value).width(1600).url()}
            alt={value?.alt ?? "文章圖片"}
            fill
            sizes="(min-width: 1024px) 800px, 100vw"
            className="object-contain"
          />
        </div>
      );
    },
    youtube: ({ value }) => {
      const input = (value?.url as string | undefined) ?? (value?.url?.toString?.() as string | undefined);
      const videoId = getYouTubeId(input);
      if (!videoId) return null;

      const title = (value?.title as string | undefined) ?? "YouTube";

      return (
        <div className="my-4 sm:my-6">
          <div className="relative w-full aspect-video rounded-2xl overflow-hidden bg-black">
            <iframe
              src={`https://www.youtube.com/embed/${videoId}`}
              title={title}
              className="absolute inset-0 h-full w-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              loading="lazy"
              referrerPolicy="strict-origin-when-cross-origin"
            />
          </div>
        </div>
      );
    },
    table: ({ value }) => {
      const rows = getTableRows(value);
      if (rows.length === 0) return null;

      return (
        <div className="my-4 sm:my-6 overflow-x-auto rounded-2xl border border-gray-200 bg-white shadow-sm">
          <table className="min-w-full border-collapse text-left text-sm">
            <tbody>
              {rows.map((row, rowIndex) => {
                const cells = Array.isArray(row?.cells) ? row.cells : [];
                const isHeaderRow =
                  Boolean(row?.isHeader) || (rowIndex === 0 && rows.length > 1);

                return (
                  <tr key={row?._key ?? `row-${rowIndex}`} className="border-b last:border-b-0">
                    {cells.map((cell, cellIndex) => {
                      const CellTag = isHeaderRow ? "th" : "td";
                      return (
                        <CellTag
                          key={`cell-${rowIndex}-${cellIndex}`}
                          scope={isHeaderRow ? "col" : undefined}
                          className={
                            isHeaderRow
                              ? "whitespace-nowrap px-4 py-3 font-semibold text-gray-900 bg-gray-50"
                              : "px-4 py-3 align-top text-gray-800"
                          }
                        >
                          {getCellText(cell)}
                        </CellTag>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      );
    },
  },
  block: {
    h1: ({ children, value }) => (
      <h1
        id={headingIdFor(value)}
        className="mt-10 mb-4 text-3xl sm:text-4xl font-bold text-[var(--blue-900)] leading-tight scroll-mt-28"
      >
        {children}
      </h1>
    ),
    h2: ({ children, value }) => (
      <h2
        id={headingIdFor(value)}
        className="mt-10 mb-4 text-2xl sm:text-3xl font-bold text-[var(--blue-900)] leading-tight scroll-mt-28"
      >
        {children}
      </h2>
    ),
    h3: ({ children, value }) => (
      <h3
        id={headingIdFor(value)}
        className="mt-8 mb-3 text-xl sm:text-2xl font-bold text-[var(--blue-900)] leading-snug scroll-mt-28"
      >
        {children}
      </h3>
    ),
    h4: ({ children, value }) => (
      <h4
        id={headingIdFor(value)}
        className="mt-6 mb-2 text-lg sm:text-xl font-semibold text-[var(--blue-900)] leading-snug scroll-mt-28"
      >
        {children}
      </h4>
    ),
    normal: ({ children }) => (
      <p className="my-4 text-gray-700 leading-relaxed">{children}</p>
    ),
    blockquote: ({ children }) => (
      <blockquote className="my-6 border-l-4 border-[var(--darker-blue)] pl-4 text-gray-700 italic">
        {children}
      </blockquote>
    ),
  },
  list: {
    bullet: ({ children }) => (
      <ul className="my-4 list-disc pl-6 space-y-2 text-gray-700">{children}</ul>
    ),
    number: ({ children }) => (
      <ol className="my-4 list-decimal pl-6 space-y-2 text-gray-700">{children}</ol>
    ),
  },
  listItem: {
    bullet: ({ children }) => <li className="leading-relaxed">{children}</li>,
    number: ({ children }) => <li className="leading-relaxed">{children}</li>,
  },
  marks: {
    strong: ({ children }) => (
      <strong className="font-semibold text-gray-900">{children}</strong>
    ),
    em: ({ children }) => <em className="italic">{children}</em>,
    link: ({ children, value }) => {
      const href = value?.href as string | undefined;
      if (!href) return <>{children}</>;

      const isExternal = /^https?:\/\//i.test(href);

      return (
        <Link
          href={href}
          target={isExternal ? "_blank" : undefined}
          rel={isExternal ? "noreferrer noopener" : undefined}
          className="text-[var(--blue-700)] underline underline-offset-4 hover:opacity-80"
        >
          {children}
        </Link>
      );
    },
  },
};

function isPortableTextValue(value: unknown): value is TypedObject | TypedObject[] {
  if (!value) return false;
  if (Array.isArray(value)) return value.every((item) => item && typeof item === "object");
  return typeof value === "object";
}

export function PostPortableText({ value }: { value: unknown }) {
  const tocContentId = useId();
  const [isTocOpen, setIsTocOpen] = useState(false);

  if (!isPortableTextValue(value)) {
    return <p className="text-gray-600">尚無文章內容</p>;
  }

  const tocItems = Array.isArray(value) ? buildToc(value) : buildToc([value]);

  return (
    <>
      {tocItems.length > 0 && (
        <nav
          aria-label="本篇文章目錄"
          className="my-6 rounded-2xl border border-[var(--blue-200)] bg-white p-5 shadow-sm"
        >
          <div className="flex items-start justify-between gap-4">
            <div className="text-2xl font-semibold text-[var(--blue-900)]">本篇目錄</div>

            <button
              type="button"
              aria-expanded={isTocOpen}
              aria-controls={tocContentId}
              onClick={() => setIsTocOpen((prev) => !prev)}
              className="shrink-0 inline-flex items-center gap-2 border border-[var(--blue-200)] bg-white px-3 py-2 text-sm font-medium text-[var(--blue-700)] hover:bg-[var(--light-blue)] transition-colors"
            >
              <span className="hidden sm:inline">{isTocOpen ? "收合" : "展開"}</span>
              <span
                aria-hidden="true"
                className={`inline-block transition-transform duration-200 ${isTocOpen ? "rotate-180" : "rotate-0"}`}
              >
                ▾
              </span>
            </button>
          </div>

          <div
            className={`mt-3 grid transition-[grid-template-rows] duration-200 ease-out ${
              isTocOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
            }`}
          >
            <div className="overflow-hidden">
              <ul
                id={tocContentId}
                className={`space-y-1 text-sm origin-top transition-[opacity,transform] duration-200 ease-out ${
                  isTocOpen ? "opacity-100 scale-100" : "opacity-0 scale-95 pointer-events-none"
                }`}
              >
                {tocItems.map((item) => {
                  const paddingLeft =
                    item.level === 2 ? "pl-0" : item.level === 3 ? "pl-4" : "pl-8";
                  return (
                    <li key={item.id} className={paddingLeft}>
                      <a
                        href={`#${item.id}`}
                        className="text-[var(--blue-700)] hover:underline underline-offset-4"
                        onClick={() => setIsTocOpen(false)}
                      >
                        {item.title}
                      </a>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </nav>
      )}

      <PortableText value={value} components={components} />
    </>
  );
}
